import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/api';
import { Message, Modal, ErrorState, EmptyState, InlineLoading, SearchableSelect, PageSkeleton } from '../components';
import { PageHeader } from '../components/layout';
import './WecomPage.css';

const defaultNotificationForm = {
  message_type: 'markdown',
  title: '企业微信通知',
  message: '',
  image_url: '',
  action_url: '',
  description: '',
  source_desc: 'Jira 通知',
  source_desc_color: '0',
  horizontalContentText: '类型 | 图文展示模板卡片',
  options_text: '同意\n不同意'
};

const runtimeStatusLabel = {
  disabled: '未启用', not_configured: '未配置', connecting: '连接中', connected: '已连接', reconnecting: '重连中', disconnected: '已断开', error: '异常', not_applicable: '不适用', never_called: '从未调用', calling: '调用中', healthy: '最近调用成功', failed: '最近调用失败'
};

const runtimeStatusBadgeClass = (status) => {
  if (['connected', 'healthy'].includes(status)) return 'badge-success';
  if (['connecting', 'reconnecting', 'calling'].includes(status)) return 'badge-warning';
  if (['disconnected', 'error', 'failed'].includes(status)) return 'badge-danger';
  return 'badge-default';
};

const formatRuntimeDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (part) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const sourceColorPreview = {
  0: '#8a8f98',
  1: '#1f2937',
  2: '#d92d20',
  3: '#16a34a'
};

const WecomPage = ({ canWrite = false }) => {
  const [activeTab, setActiveTab] = useState('config');
  const [config, setConfig] = useState({
    name: '默认企业微信配置',
    bot_id: '',
    bot_secret: '',
    webhook_url: '',
    notification_webhook_url: '',
    enter_chat_message: '',
    message_handler_type: 'jira_assistant',
    event_handler_types: ['enter_chat', 'template_card_event'],
    agent_config_id: '',
    external_agent_endpoint: '',
    external_agent_secret: '',
    is_active: false,
    gateway_enabled: false,
    notification_enabled: true,
    has_bot_secret: false
  });
  const [botConfigs, setBotConfigs] = useState([]);
  const [runtimeStatuses, setRuntimeStatuses] = useState([]);
  const [agentConfigs, setAgentConfigs] = useState([]);
  const [agentConfigError, setAgentConfigError] = useState('');
  const [bindings, setBindings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [alertState, setAlertState] = useState({ isOpen: false, message: '' });
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [bindingQuery, setBindingQuery] = useState('');
  const [bindingBot, setBindingBot] = useState('');
  const [bindingStatus, setBindingStatus] = useState('');
  const [notificationQuery, setNotificationQuery] = useState('');
  const [notificationType, setNotificationType] = useState('');
  const [notificationForm, setNotificationForm] = useState(defaultNotificationForm);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [selectedBotConfigId, setSelectedBotConfigId] = useState(null);
  const [notificationDetail, setNotificationDetail] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshRuntimeStatuses = async () => {
    try {
      const response = await api.getWecomRuntimeStatuses();
      setRuntimeStatuses(response.data.items || []);
    } catch (runtimeError) {
      // Runtime health is supplementary; preserve configuration access when it is unavailable.
    }
  };

  useEffect(() => {
    refreshRuntimeStatuses();
    const timer = window.setInterval(refreshRuntimeStatuses, 5000);
    return () => window.clearInterval(timer);
  }, []);

  const activeBindings = useMemo(
    () => bindings.filter(binding => binding.is_active && binding.wecom_user_id),
    [bindings]
  );

  const selectedBotBindings = useMemo(
    () => activeBindings.filter(binding => String(binding.bot_config_id || '') === String(selectedBotConfigId || '')),
    [activeBindings, selectedBotConfigId]
  );

  const fetchData = async () => {
    try {
      setLoading(true);
      const [configRes, bindingsRes, notificationsRes] = await Promise.all([
        api.getWecomConfig(),
        api.getWecomBindings(),
        api.getWecomNotifications({ page_size: 50 })
      ]);
      let nextAgentConfigs = [];
      let nextAgentError = '';
      if (canWrite) {
        try {
          const agentConfigsRes = await api.getWecomAgents();
          nextAgentConfigs = Array.isArray(agentConfigsRes.data)
            ? agentConfigsRes.data
            : [];
        } catch (agentError) {
          nextAgentError = agentError.response?.data?.detail || agentError.message;
        }
      }
      setConfig(prev => ({ ...prev, ...configRes.data }));
      const configs = configRes.data.items || [configRes.data].filter(item => item && item.id);
      setBotConfigs(configs);
      setAgentConfigs(nextAgentConfigs);
      setAgentConfigError(nextAgentError);
      setSelectedBotConfigId(prev => prev || configs.find(item => item.is_active)?.id || configs[0]?.id || null);
      setBindings(bindingsRes.data.items || []);
      setNotifications(notificationsRes.data.items || []);
      setLoaded(true);
      setError(null);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      const payload = {
        id: config.id,
        name: config.name,
        bot_id: config.bot_id,
        enter_chat_message: config.enter_chat_message,
        message_handler_type: config.message_handler_type || 'jira_assistant',
        event_handler_types: config.event_handler_types || [],
        agent_config_id: config.agent_config_id || null,
        external_agent_endpoint: config.external_agent_endpoint,
        is_active: config.is_active,
        gateway_enabled: config.gateway_enabled,
        notification_enabled: config.notification_enabled
      };
      if (config.bot_secret) payload.bot_secret = config.bot_secret;
      if (config.external_agent_secret) payload.external_agent_secret = config.external_agent_secret;
      const response = await api.updateWecomConfig(payload);
      setConfig(prev => ({ ...prev, ...response.data.config, bot_secret: '' }));
      await fetchData();
      setAlertState({ isOpen: true, message: '企业微信配置已保存' });
    } catch (err) {
      setAlertState({ isOpen: true, message: '保存失败：' + (err.response?.data?.detail || err.message) });
    } finally {
      setSaving(false);
    }
  };

  const testConfig = async () => {
    try {
      const response = await api.testWecomConfig(config.id ? { bot_config_id: config.id } : undefined);
      setAlertState({ isOpen: true, message: response.data.connected ? '机器人已连接' : '机器人配置校验通过，当前未建立连接' });
    } catch (err) {
      setAlertState({ isOpen: true, message: '测试失败：' + (err.response?.data?.detail || err.message) });
    }
  };

  const toggleEventHandlerType = (eventType) => {
    setConfig(prev => {
      const current = prev.event_handler_types || [];
      return {
        ...prev,
        event_handler_types: current.includes(eventType)
          ? current.filter(item => item !== eventType)
          : [...current, eventType]
      };
    });
  };

  const selectConfig = (bot) => {
    setConfig(prev => ({ ...prev, ...bot, bot_secret: '', external_agent_secret: '' }));
  };

  const createDraftConfig = () => {
    setConfig({
      name: '新的企业微信 Bot',
      bot_id: '',
      bot_secret: '',
      webhook_url: '',
      notification_webhook_url: config.notification_webhook_url,
      enter_chat_message: '',
      message_handler_type: 'jira_assistant',
      event_handler_types: ['enter_chat', 'template_card_event'],
      agent_config_id: '',
      external_agent_endpoint: '',
      external_agent_secret: '',
      is_active: false,
      gateway_enabled: false,
      notification_enabled: false,
      has_bot_secret: false
    });
  };

  const openNotificationModal = () => {
    const botConfigId = selectedBotConfigId || botConfigs.find(item => item.is_active)?.id || botConfigs[0]?.id || null;
    setSelectedBotConfigId(botConfigId);
    setSelectedRecipients(activeBindings
      .filter(binding => String(binding.bot_config_id || '') === String(botConfigId || ''))
      .map(binding => binding.wecom_user_id));
    setNotificationForm(defaultNotificationForm);
    setShowNotificationModal(true);
  };

  const toggleSelectAllRecipients = () => {
    const activeUserIds = selectedBotBindings.map(binding => binding.wecom_user_id);
    setSelectedRecipients(prev => (
      prev.length === activeUserIds.length ? [] : activeUserIds
    ));
  };

  const parseHorizontalContentList = (text) => (
    (text || '')
      .split('\n')
      .map(line => {
        const [keyname, value, url] = line.split('|').map(item => item.trim());
        return keyname || value ? { keyname: keyname || '', value: value || '', url: url || '' } : null;
      })
      .filter(Boolean)
  );

  const buildNewsTemplatePayload = () => {
    const horizontalContentList = parseHorizontalContentList(notificationForm.horizontalContentText);
    const payload = {
      description: notificationForm.description,
      source_desc: notificationForm.source_desc,
      source_desc_color: Number(notificationForm.source_desc_color || 0),
      image_url: notificationForm.image_url,
      action_url: notificationForm.action_url
    };
    if (horizontalContentList.length > 0) {
      payload.horizontal_content_list = horizontalContentList;
    }
    return payload;
  };

  const buildTemplatePayload = () => {
    if (notificationForm.message_type === 'news_notice') {
      return buildNewsTemplatePayload();
    }
    if (notificationForm.message_type === 'vote_interaction') {
      return {
        description: notificationForm.description,
        question_key: 'vote',
        mode: 0,
        options: notificationForm.options_text
          .split('\n')
          .map((text, index) => ({ id: `option_${index + 1}`, text: text.trim(), is_checked: false }))
          .filter(option => option.text),
        submit_text: '提交'
      };
    }
    return { content: notificationForm.message };
  };

  const sendWecomNotification = async () => {
    if (selectedRecipients.length === 0) {
      setAlertState({ isOpen: true, message: '请选择通知接收人' });
      return;
    }
    if (!notificationForm.title.trim()) {
      setAlertState({ isOpen: true, message: '请输入通知标题' });
      return;
    }
    if (notificationForm.message_type === 'markdown' && !notificationForm.message.trim()) {
      setAlertState({ isOpen: true, message: '请输入 Markdown 通知内容' });
      return;
    }

    try {
      setSending(true);
      const response = await api.sendWecomNotification({
        wecom_user_ids: selectedRecipients,
        bot_config_id: selectedBotConfigId,
        message_type: notificationForm.message_type,
        title: notificationForm.title,
        message: notificationForm.message || notificationForm.description,
        template_payload: buildTemplatePayload()
      });
      const notification = response.data.notification;
      setShowNotificationModal(false);
      await fetchData();
      setAlertState({ isOpen: true, message: `通知已创建并发送：${notification?.title || notificationForm.title}` });
    } catch (err) {
      setAlertState({ isOpen: true, message: '发送失败：' + (err.response?.data?.detail || err.message) });
    } finally {
      setSending(false);
    }
  };

  const openNotificationDetail = async (notification) => {
    try {
      const response = await api.getWecomNotification(notification.id);
      setNotificationDetail(response.data);
    } catch (err) {
      setAlertState({ isOpen: true, message: '加载通知详情失败：' + (err.response?.data?.detail || err.message) });
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('zh-CN', { hour12: false });
  };

  const messageTypeLabel = (type) => ({
    markdown: 'Markdown',
    news_notice: '图文展示模板卡片',
    vote_interaction: '投票选择模板卡片'
  }[type] || type);

  const creatorLabel = (notification) => (
    notification?.created_by_name
    || notification?.created_by_email
    || (notification?.created_by ? `用户 ${notification.created_by}` : '-')
  );

  const renderJsonBlock = (value) => (
    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px', lineHeight: 1.5 }}>
      {JSON.stringify(value || {}, null, 2)}
    </pre>
  );

  const getNotificationTemplatePayload = (detail) => (
    detail?.message_detail?.template_payload
    || detail?.notification?.template_payload
    || detail?.message_detail?.preview_payload?.template_card
    || detail?.notification?.preview_payload?.template_card
    || {}
  );

  const renderSentNotificationPreview = (detail) => {
    const messageType = detail?.notification?.message_type;
    const payload = getNotificationTemplatePayload(detail);

    if (messageType === 'markdown') {
      return (
        <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text-primary)', lineHeight: 1.7 }}>
          {payload.content || detail?.message_detail?.content || 'Markdown 内容预览'}
        </div>
      );
    }

    const source = payload.source || {};
    const mainTitle = payload.main_title || {};
    const sourceColor = sourceColorPreview[source.desc_color] || sourceColorPreview[0];

    if (messageType === 'news_notice') {
      const cardImage = payload.card_image || {};
      const horizontalItems = payload.horizontal_content_list || [];
      return (
        <div>
          <div style={{ color: sourceColor, fontSize: '12px', marginBottom: '8px', fontWeight: 600 }}>{source.desc || 'Jira 通知'}</div>
          {cardImage.url && (
            <div style={{ width: '100%', aspectRatio: `${cardImage.aspect_ratio || 2.25} / 1`, backgroundImage: `url(${cardImage.url})`, backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: '6px', marginBottom: '12px' }} />
          )}
          <div style={{ fontWeight: 700, marginBottom: '6px' }}>{mainTitle.title || detail?.notification?.title || '图文展示模板卡片'}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>{mainTitle.desc || '卡片描述'}</div>
          {horizontalItems.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border-color)', marginTop: '12px', paddingTop: '8px' }}>
              {horizontalItems.map((item, index) => (
                <div key={`${item.keyname || 'item'}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '13px', padding: '4px 0' }}>
                  <span style={{ color: 'var(--text-muted)' }}>{item.keyname || '-'}</span>
                  <span>{item.value || '-'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (messageType === 'vote_interaction') {
      const checkbox = payload.checkbox || {};
      const options = checkbox.option_list || [];
      return (
        <div>
          <div style={{ color: sourceColor, fontSize: '12px', marginBottom: '8px', fontWeight: 600 }}>{source.desc || 'Jira 通知'}</div>
          <div style={{ fontWeight: 700, marginBottom: '6px' }}>{mainTitle.title || detail?.notification?.title || '投票选择模板卡片'}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '12px', lineHeight: 1.6 }}>{mainTitle.desc || '投票说明'}</div>
          {options.map((option, index) => (
            <label key={option.id || index} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0' }}>
              <input type="checkbox" checked={!!option.is_checked} readOnly />
              <span>{option.text || option.id || '-'}</span>
            </label>
          ))}
          <button type="button" className="btn btn-primary" disabled style={{ marginTop: '8px' }}>
            {payload.submit_button?.text || '提交'}
          </button>
        </div>
      );
    }

    return renderJsonBlock(payload);
  };

  const renderPreview = () => {
    if (notificationForm.message_type === 'markdown') {
      return (
        <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text-primary)' }}>
          {notificationForm.message || 'Markdown 内容预览'}
        </div>
      );
    }
    if (notificationForm.message_type === 'news_notice') {
      const horizontalItems = parseHorizontalContentList(notificationForm.horizontalContentText);
      return (
        <div>
          <div style={{ color: sourceColorPreview[notificationForm.source_desc_color] || sourceColorPreview[0], fontSize: '12px', marginBottom: '8px', fontWeight: 600 }}>{notificationForm.source_desc || 'Jira 通知'}</div>
          {notificationForm.image_url && (
            <div style={{ width: '100%', aspectRatio: '2.25 / 1', backgroundImage: `url(${notificationForm.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: '6px', marginBottom: '12px' }} />
          )}
          <div style={{ fontWeight: 700, marginBottom: '6px' }}>{notificationForm.title || '图文展示模板卡片'}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>{notificationForm.description || notificationForm.message || '卡片描述'}</div>
          {horizontalItems.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border-color)', marginTop: '12px', paddingTop: '8px' }}>
              {horizontalItems.map((item, index) => (
                <div key={`${item.keyname}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '13px', padding: '4px 0' }}>
                  <span style={{ color: 'var(--text-muted)' }}>{item.keyname || '-'}</span>
                  <span>{item.value || '-'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    return (
      <div>
        <div style={{ fontWeight: 700, marginBottom: '6px' }}>{notificationForm.title || '投票选择模板卡片'}</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '12px' }}>{notificationForm.description || '投票说明'}</div>
        {notificationForm.options_text.split('\n').filter(Boolean).map((option, index) => (
          <label key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0' }}>
            <input type="checkbox" readOnly />
            <span>{option}</span>
          </label>
        ))}
        <button type="button" className="btn btn-primary" disabled style={{ marginTop: '8px' }}>提交</button>
      </div>
    );
  };

  const visibleBindings = bindings.filter(binding => {
    const text = [binding.wecom_name, binding.wecom_user_id, binding.jira_email, binding.jira_account_id, binding.bot_name].filter(Boolean).join(' ').toLowerCase();
    return text.includes(bindingQuery.trim().toLowerCase())
      && (!bindingBot || String(binding.bot_config_id) === bindingBot)
      && (!bindingStatus || (bindingStatus === 'bound' ? binding.has_jira_binding : !binding.has_jira_binding));
  });
  const visibleNotifications = notifications.filter(item => (item.title || '').toLowerCase().includes(notificationQuery.trim().toLowerCase())
    && (!notificationType || item.message_type === notificationType));
  const notificationTypes = [{ value: 'markdown', label: 'Markdown' }, { value: 'news_notice', label: '图文展示模板卡片' }, { value: 'vote_interaction', label: '投票选择模板卡片' }];
  const notificationStatus = value => ({ pending: '待发送', queued: '排队中', sending: '发送中', sent: '已发送', success: '成功', failed: '失败', partial: '部分失败', partial_failed: '部分失败' }[value] || value);
  const notificationBadge = value => ['sent', 'success'].includes(value) ? 'badge-success' : value === 'failed' ? 'badge-danger' : ['partial', 'partial_failed'].includes(value) ? 'badge-warning' : 'badge-default';

  if (error && !loaded) {
    return <div className="configuration-list-page wecom-integration-page"><PageHeader title="企业微信" /><ErrorState title="无法加载企业微信" description={error} onRetry={fetchData} /></div>;
  }

  if (loading && !loaded) {
    return <div className="configuration-list-page wecom-integration-page"><PageHeader title="企业微信" /><PageSkeleton rowCount={5} label="加载企业微信配置中" /></div>;
  }

  return (
    <div className="configuration-list-page wecom-integration-page">
      <Message type="error" message={error} onClose={() => setError(null)} />
      <PageHeader
        title="企业微信"
      />

      <div className="admin-page-tabs" role="tablist" aria-label="企业微信设置分类">
        {[
          ['config', '配置'],
          ['bindings', '绑定用户'],
          ['notifications', '主动通知']
        ].map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={activeTab === id} onClick={() => setActiveTab(id)}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'config' && (
        <section className="configuration-settings-section wecom-config-section">
          <div className="configuration-settings-section-header">
            <h3 style={{ margin: 0 }}>企业微信配置</h3>
            {canWrite && (
              <button className="btn btn-secondary" onClick={createDraftConfig}>新建 Bot</button>
            )}
          </div>
          <div className="configuration-settings-section-body">
            {botConfigs.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                {botConfigs.map(bot => (
                  <button key={bot.id} type="button" className={String(config.id) === String(bot.id) ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => selectConfig(bot)}>
                    {bot.name || bot.bot_id || `Bot ${bot.id}`}
                  </button>
                ))}
              </div>
            )}
            {config.id && (() => {
              const runtime = runtimeStatuses.find((item) => String(item.bot_config_id) === String(config.id));
              if (!runtime) return null;
              return (
                <div className="wecom-runtime-status" aria-label="企业微信运行状态">
                  <div className="wecom-runtime-status-row">
                    <strong className="wecom-runtime-status-title">Bot 长连接</strong>
                    <span className={`badge wecom-runtime-status-badge ${runtimeStatusBadgeClass(runtime.bot_status)}`}>
                      {runtimeStatusLabel[runtime.bot_status] || runtime.bot_status || '未知'}
                    </span>
                    <div className="wecom-runtime-status-details">
                      <span className="wecom-runtime-status-detail">
                        <small>最后连接</small>
                        <span>{formatRuntimeDateTime(runtime.bot_last_connected_at) || '暂无连接记录'}</span>
                      </span>
                      <span className="wecom-runtime-status-detail">
                        <small>重连</small>
                        <span>{runtime.bot_reconnect_attempt || 0} 次</span>
                      </span>
                    </div>
                    {runtime.bot_error_message && (
                      <small className="wecom-runtime-status-error">原因：{runtime.bot_error_message}</small>
                    )}
                  </div>
                  <div className="wecom-runtime-status-row">
                    <strong className="wecom-runtime-status-title">Agent 调用健康度</strong>
                    <span className={`badge wecom-runtime-status-badge ${runtimeStatusBadgeClass(runtime.agent_status)}`}>
                      {runtimeStatusLabel[runtime.agent_status] || runtime.agent_status || '未知'}
                    </span>
                    <div className="wecom-runtime-status-details">
                      <span className="wecom-runtime-status-detail">
                        <small>最近调用</small>
                        <span>{formatRuntimeDateTime(runtime.agent_last_called_at) || '暂无调用记录'}</span>
                      </span>
                    </div>
                    {runtime.agent_error_message && (
                      <small className="wecom-runtime-status-error">原因：{runtime.agent_error_message}</small>
                    )}
                  </div>
                </div>
              );
            })()}
            <fieldset
              disabled={!canWrite}
              style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
            >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              {[
                ['name', '配置名称'],
                ['bot_id', 'Bot ID']
              ].map(([field, label]) => (
                <div className="form-group" key={field}>
                  <label className="form-label">{label}</label>
                  <input className="form-input" value={config[field] || ''} onChange={(e) => setConfig(prev => ({ ...prev, [field]: e.target.value }))} />
                </div>
              ))}
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">状态提醒专用 Webhook URL</label>
                <input className="form-input" value={config.notification_webhook_url || ''} readOnly />
              </div>
              <div className="form-group">
                <label className="form-label">消息处理逻辑</label>
                <select
                  className="form-input"
                  value={config.message_handler_type || 'jira_assistant'}
                  onChange={(e) => setConfig(prev => ({ ...prev, message_handler_type: e.target.value }))}
                >
                  <option value="jira_assistant">Jira 助手</option>
                  <option value="agent">AI 设置 Agent</option>
                  <option value="external_agent">外部 Agent</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">事件处理</label>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', minHeight: '40px', alignItems: 'center' }}>
                  {[
                    ['enter_chat', '进入会话'],
                    ['template_card_event', '模板卡片事件']
                  ].map(([value, label]) => (
                    <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input
                        type="checkbox"
                        checked={(config.event_handler_types || []).includes(value)}
                        onChange={() => toggleEventHandlerType(value)}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">进入会话欢迎文案</label>
                <textarea
                  className="form-input"
                  rows={5}
                  value={config.enter_chat_message || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, enter_chat_message: e.target.value }))}
                />
              </div>
              {config.message_handler_type === 'external_agent' && (
                <>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">外部 Agent Endpoint</label>
                    <input
                      className="form-input"
                      value={config.external_agent_endpoint || ''}
                      onChange={(e) => setConfig(prev => ({ ...prev, external_agent_endpoint: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">外部 Agent Secret</label>
                    <input
                      type="password"
                      className="form-input"
                      value={config.external_agent_secret || ''}
                      placeholder={config.has_external_agent_secret ? '已配置，留空表示不修改' : '可选'}
                      onChange={(e) => setConfig(prev => ({ ...prev, external_agent_secret: e.target.value }))}
                    />
                  </div>
                </>
              )}
              {config.message_handler_type === 'agent' && (
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">AI 设置 Agent</label>
                  {agentConfigError && (
                    <div className="form-help" style={{ color: 'var(--danger)' }}>
                      {agentConfigError}
                    </div>
                  )}
                  <select
                    className="form-input"
                    value={config.agent_config_id || ''}
                    onChange={(e) => setConfig(prev => ({ ...prev, agent_config_id: e.target.value }))}
                  >
                    <option value="">请选择 Agent</option>
                    {agentConfigs.map(agent => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name || `Agent ${agent.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Bot Secret</label>
                <input
                  type="password"
                  className="form-input"
                  value={config.bot_secret || ''}
                  placeholder={config.has_bot_secret ? '已配置，留空表示不修改' : '请输入 Bot Secret'}
                  onChange={(e) => setConfig(prev => ({ ...prev, bot_secret: e.target.value }))}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '16px', marginTop: '16px', flexWrap: 'wrap' }}>
              {[
                ['is_active', '启用配置'],
                ['gateway_enabled', '启用长连接网关'],
                ['notification_enabled', '启用机器人状态提醒']
              ].map(([field, label]) => (
                <label key={field} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="checkbox" checked={!!config[field]} onChange={(e) => setConfig(prev => ({ ...prev, [field]: e.target.checked }))} />
                  <span>{label}</span>
                </label>
              ))}
            </div>

            {canWrite && <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button className="btn btn-primary" onClick={saveConfig} disabled={saving}>{saving ? '保存中...' : '保存配置'}</button>
              <button className="btn btn-secondary" onClick={testConfig}>测试连接</button>
            </div>}
            </fieldset>
          </div>
        </section>
      )}

      {activeTab === 'bindings' && (
        <section className="configuration-list-surface wecom-bindings-surface">
          <div className="configuration-list-surface-header">
            <div><h3>当前绑定用户</h3><span className="wecom-list-count">显示 {visibleBindings.length} / {bindings.length} 人</span></div>
            <button className="btn btn-secondary" onClick={fetchData} disabled={loading}>{loading ? <InlineLoading label="刷新中" /> : '刷新'}</button>
          </div>
          <div className="configuration-list-surface-body">
            <div className="wecom-list-filters">
              <label className="form-group"><span className="form-label">搜索用户</span><input className="form-input" type="search" aria-label="搜索绑定用户" placeholder="姓名、用户 ID 或 Jira 邮箱" value={bindingQuery} onChange={e => setBindingQuery(e.target.value)} /></label>
              <div className="form-group" role="group" aria-label="绑定用户 Bot"><span className="form-label">Bot</span><SearchableSelect value={bindingBot} onChange={setBindingBot} options={[{ value: '', label: '全部 Bot' }, ...botConfigs.map(bot => ({ value: String(bot.id), label: bot.name || bot.bot_id }))]} /></div>
              <div className="form-group" role="group" aria-label="Jira 绑定状态"><span className="form-label">Jira 绑定</span><SearchableSelect value={bindingStatus} onChange={setBindingStatus} options={[{ value: '', label: '全部状态' }, { value: 'bound', label: '已绑定' }, { value: 'unbound', label: '未绑定 Jira' }]} /></div>
            </div>
            {visibleBindings.length === 0 ? (
              <EmptyState title={bindings.length ? '没有匹配的绑定用户' : '暂无绑定用户'} description={bindings.length ? '请调整搜索或筛选条件。' : '用户添加或与 Bot 对话后会显示在这里。'} />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>企业微信用户ID</th>
                      <th>Bot</th>
                      <th>企业微信名称</th>
                      <th>Jira 邮箱</th>
                      <th>Jira Account ID</th>
                      <th>Jira 绑定</th>
                      <th>通知状态</th>
                      <th>更新时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleBindings.map(binding => (
                      <tr key={binding.id || binding.wecom_user_id}>
                        <td>{binding.wecom_user_id || '-'}</td>
                        <td>{binding.bot_name || binding.bot_id || (binding.bot_config_id ? `Bot ${binding.bot_config_id}` : '-')}</td>
                        <td>{binding.wecom_name || '-'}</td>
                        <td>{binding.jira_email || '-'}</td>
                        <td>{binding.jira_account_id || '-'}</td>
                        <td><span className={`badge ${binding.has_jira_binding ? 'badge-success' : 'badge-default'}`}>{binding.has_jira_binding ? '已绑定' : '未绑定 Jira'}</span></td>
                        <td><span className={`badge ${binding.is_active ? 'badge-success' : 'badge-default'}`}>{binding.is_active ? '可通知' : '停用'}</span></td>
                        <td>{formatDateTime(binding.updated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {activeTab === 'notifications' && (
        <section className="configuration-list-surface wecom-notifications-surface">
          <div className="configuration-list-surface-header">
            <div><h3>通知列表</h3><span className="wecom-list-count">最近 50 条内 · 显示 {visibleNotifications.length} 条</span></div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {canWrite && (
                <button className="btn btn-primary" onClick={openNotificationModal} disabled={activeBindings.length === 0}>主动发送通知</button>
              )}
              <button className="btn btn-secondary" onClick={fetchData} disabled={loading}>{loading ? <InlineLoading label="刷新中" /> : '刷新'}</button>
            </div>
          </div>
          <div className="configuration-list-surface-body">
            <div className="wecom-list-filters">
              <label className="form-group"><span className="form-label">搜索通知</span><input className="form-input" type="search" aria-label="搜索通知标题" placeholder="输入通知标题" value={notificationQuery} onChange={e => setNotificationQuery(e.target.value)} /></label>
              <div className="form-group" role="group" aria-label="通知类型筛选"><span className="form-label">通知类型</span><SearchableSelect value={notificationType} onChange={setNotificationType} options={[{ value: '', label: '全部类型' }, ...notificationTypes]} /></div>
            </div>
            {visibleNotifications.length === 0 ? (
              <EmptyState title={notifications.length ? '没有匹配的通知' : '暂无主动通知'} description={notifications.length ? '请调整搜索或筛选条件。' : '发送后的通知及发送结果会显示在这里。'} />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>标题</th>
                      <th>类型</th>
                      <th>状态</th>
                      <th>收件人</th>
                      <th>成功/失败</th>
                      <th>投票</th>
                      <th>创建人</th>
                      <th>创建时间</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleNotifications.map(notification => (
                      <tr key={notification.id}>
                        <td>{notification.title}</td>
                        <td>{messageTypeLabel(notification.message_type)}</td>
                        <td><span className={`badge ${notificationBadge(notification.status)}`}>{notificationStatus(notification.status)}</span></td>
                        <td>{notification.recipients?.length || 0}</td>
                        <td><span className="wecom-count-success">{notification.sent_count || 0}</span> / <span className={notification.failed_count ? 'wecom-count-failed' : ''}>{notification.failed_count || 0}</span></td>
                        <td>{notification.vote_count || 0}</td>
                        <td>{creatorLabel(notification)}</td>
                        <td>{formatDateTime(notification.created_at)}</td>
                        <td><button className="btn btn-secondary" onClick={() => openNotificationDetail(notification)}>详情</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      <Modal
        isOpen={showNotificationModal}
        title="新建主动通知"
        busy={sending}
        className="wecom-notification-modal"
        onClose={() => { if (!sending) setShowNotificationModal(false); }}
        size="xlarge"
        buttons={[
          { label: '取消', onClick: () => setShowNotificationModal(false), variant: 'default', disabled: sending },
          { label: sending ? <InlineLoading label="发送中" /> : '发送通知', onClick: sendWecomNotification, variant: 'primary', disabled: sending }
        ]}
      >
        <div className="wecom-compose-layout">
          <div>
            <div className="form-group">
              <label className="form-label">通知类型</label>
              <SearchableSelect value={notificationForm.message_type} disabled={sending} onChange={value => setNotificationForm(prev => ({ ...prev, message_type: value }))} options={notificationTypes} />
            </div>
            <div className="form-group">
              <label className="form-label">标题</label>
              <input className="form-input" value={notificationForm.title} onChange={(e) => setNotificationForm(prev => ({ ...prev, title: e.target.value }))} />
            </div>
            {notificationForm.message_type === 'markdown' ? (
              <div className="form-group">
                <label className="form-label">Markdown 内容</label>
                <textarea className="form-input" rows={7} value={notificationForm.message} onChange={(e) => setNotificationForm(prev => ({ ...prev, message: e.target.value }))} />
              </div>
            ) : (
              <>
                {notificationForm.message_type === 'news_notice' && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 120px', gap: '12px' }}>
                      <div className="form-group">
                        <label className="form-label">Source 文案</label>
                        <input className="form-input" value={notificationForm.source_desc} onChange={(e) => setNotificationForm(prev => ({ ...prev, source_desc: e.target.value }))} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Source 颜色</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 28px', gap: '8px', alignItems: 'center' }}>
                          <SearchableSelect value={notificationForm.source_desc_color} disabled={sending} searchable={false} onChange={value => setNotificationForm(prev => ({ ...prev, source_desc_color: value }))} options={['灰色', '黑色', '红色', '绿色'].map((label, value) => ({ value: String(value), label }))} />
                          <span aria-label="Source 颜色预览" style={{ width: '24px', height: '24px', borderRadius: '4px', border: '1px solid var(--border-color)', background: sourceColorPreview[notificationForm.source_desc_color] || sourceColorPreview[0] }} />
                        </div>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">描述</label>
                      <textarea className="form-input" rows={3} value={notificationForm.description} onChange={(e) => setNotificationForm(prev => ({ ...prev, description: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">图片 URL</label>
                      <input className="form-input" value={notificationForm.image_url} onChange={(e) => setNotificationForm(prev => ({ ...prev, image_url: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">跳转 URL</label>
                      <input className="form-input" value={notificationForm.action_url} onChange={(e) => setNotificationForm(prev => ({ ...prev, action_url: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">横向内容</label>
                      <textarea className="form-input" rows={4} value={notificationForm.horizontalContentText} onChange={(e) => setNotificationForm(prev => ({ ...prev, horizontalContentText: e.target.value }))} />
                    </div>
                  </>
                )}
                {notificationForm.message_type === 'vote_interaction' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">描述</label>
                      <textarea className="form-input" rows={3} value={notificationForm.description} onChange={(e) => setNotificationForm(prev => ({ ...prev, description: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">投票选项</label>
                      <textarea className="form-input" rows={5} value={notificationForm.options_text} onChange={(e) => setNotificationForm(prev => ({ ...prev, options_text: e.target.value }))} />
                    </div>
                  </>
                )}
              </>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">使用 Bot</label>
                <SearchableSelect value={String(selectedBotConfigId || '')} disabled={sending} options={botConfigs.map(bot => ({ value: String(bot.id), label: bot.name || bot.bot_id || `Bot ${bot.id}` }))} onChange={value => {
                  setSelectedBotConfigId(Number(value));
                  setSelectedRecipients(activeBindings.filter(binding => String(binding.bot_config_id) === value).map(binding => binding.wecom_user_id));
                }} />
              </div>
              <div className="form-group">
                <label className="form-label">接收人</label>
                <SearchableSelect multiple clearable value={selectedRecipients} onChange={setSelectedRecipients} disabled={sending} placeholder="搜索接收人" options={selectedBotBindings.map(binding => ({ value: binding.wecom_user_id, label: binding.wecom_name || binding.wecom_user_id, searchText: `${binding.wecom_user_id} ${binding.jira_email || ''}` }))} />
                <div className="wecom-recipient-summary"><span>已选 {selectedRecipients.length} / {selectedBotBindings.length} 人</span><button type="button" className="btn btn-ghost btn-sm" disabled={sending || !selectedBotBindings.length} onClick={toggleSelectAllRecipients}>{selectedRecipients.length === selectedBotBindings.length ? '取消全选' : '全选'}</button></div>
              </div>
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 700, marginBottom: '10px' }}>预览通知</div>
            <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', background: 'var(--bg-secondary)', minHeight: '220px' }}>
              {renderPreview()}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!notificationDetail}
        title="通知详情"
        onClose={() => setNotificationDetail(null)}
        size="large"
        buttons={[{ label: '关闭', onClick: () => setNotificationDetail(null), variant: 'default' }]}
      >
        {notificationDetail && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
              <div><strong>标题</strong><br />{notificationDetail.notification.title}</div>
              <div><strong>类型</strong><br />{messageTypeLabel(notificationDetail.notification.message_type)}</div>
              <div><strong>状态</strong><br />{notificationDetail.notification.status}</div>
              <div><strong>创建人</strong><br />{creatorLabel(notificationDetail.notification)}</div>
              <div><strong>创建时间</strong><br />{formatDateTime(notificationDetail.notification.created_at)}</div>
            </div>

            <h4>通知内容</h4>
            <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px', background: 'var(--bg-secondary)', marginBottom: '12px' }}>
              {renderSentNotificationPreview(notificationDetail)}
            </div>
            <details style={{ marginBottom: '20px' }}>
              <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)', fontWeight: 600 }}>发送 Payload</summary>
              <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px', background: 'var(--bg-secondary)', marginTop: '8px' }}>
                {renderJsonBlock(getNotificationTemplatePayload(notificationDetail))}
              </div>
            </details>

            <h4>发送结果</h4>
            <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
              <table className="table">
                <thead><tr><th>用户</th><th>状态</th><th>错误</th><th>时间</th></tr></thead>
                <tbody>
                  {(notificationDetail.logs || []).map(log => (
                    <tr key={log.id}><td>{log.recipient}</td><td>{log.status}</td><td>{log.error_message || '-'}</td><td>{formatDateTime(log.created_at)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            {notificationDetail.notification.message_type === 'vote_interaction' && (
              <>
                <h4>投票统计</h4>
                {notificationDetail.vote_statistics && (
                  <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>
                      {notificationDetail.vote_statistics.question_text || '投票问题'} · 共 {notificationDetail.vote_statistics.total_votes || 0} 人投票 · 共 {notificationDetail.vote_statistics.total_selections || 0} 次选择
                    </div>
                    <table className="table">
                      <thead><tr><th>选项</th><th>票数</th><th>占比</th></tr></thead>
                      <tbody>
                        {(notificationDetail.vote_statistics.options || []).map(option => (
                          <tr key={option.id}>
                            <td>{option.text || option.id}</td>
                            <td>{option.count || 0}</td>
                            <td>{option.percent || 0}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <h4>投票信息</h4>
                {(notificationDetail.votes || []).length === 0 ? (
                  <div style={{ color: 'var(--text-muted)' }}>暂无投票回传。</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table">
                      <thead><tr><th>用户</th><th>问题</th><th>选择选项</th><th>时间</th></tr></thead>
                      <tbody>
                        {notificationDetail.votes.map(vote => (
                          <tr key={vote.id}>
                            <td>{vote.wecom_user_id}</td>
                            <td>{vote.question_text || '-'}</td>
                            <td>{(vote.selected_option_texts || []).join('、') || '-'}</td>
                            <td>{formatDateTime(vote.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      <Message type="info" message={alertState.isOpen ? alertState.message : ''} onClose={() => setAlertState({ isOpen: false, message: '' })} />
    </div>
  );
};

export default WecomPage;
